import { useState, useRef, useEffect } from "react";
import {
  X, Loader2, CheckCircle2, ShieldCheck, ChevronDown, ChevronUp,
  Building2, UserRound, Landmark, Wrench, LifeBuoy, Check, Plane, Fuel,
} from "lucide-react";
import PerfilCampos from "../components/PerfilCampos";
import DocsCredenciamento from "../components/DocsCredenciamento";
import { supabase } from "../lib/supabase";
import { uploadToStorage, prepararUpload } from "../lib/storage";
import { lerRascunho, gravarRascunho, apagarRascunho } from "../lib/rascunho";

import { generateId, getToday, maskCNPJ, maskCEP, maskPhone, fetchCNPJ, fetchCEP, fetchCPF } from "../utils";
import { decodeFichaToken } from "../lib/fichaLink";
import { registrarAceite } from "../lib/aceite";
import { tabelaDoConvite, ufsCondicaoExclusiva } from "../lib/tabelasComerciais";
import { sincronizarCadastro } from "../lib/manutencaoSync";
import { emitirCartaoCNPJ } from "../lib/cnpjComprovante";
import { enfileirar } from "../lib/filaGravacao";
import { reenviarPendentes } from "../lib/database";
import { fetchANP, anpParaPerfil } from "../lib/anp";
import { fetchANAC, anacParaPerfil, SITUACOES_IMPEDITIVAS } from "../lib/anac";
import TermosAceite from "../components/TermosAceite";
import {
  TIPOS_ESTABELECIMENTO, TIPOS_VEICULO, BRAZILIAN_STATES,
  SERVICOS_PRESTADOS, ASSISTENCIA_TIPOS, DIAS_SEMANA, DIAS_UTEIS, perfilDe, resumoPerfil, tipoPorAtividade,
  docsDaFicha, DOC_ASSINANTE, assinanteEhRepresentante, secoesDaFicha,
  modeloDaFicha, faltandoNoPerfil, limparPerfil, tituloSecaoServicos, condicoesADefinir,
} from "../constants";

// O que a ANAC devolve completa o que a pessoa já marcou, não substitui: quem
// marcou "Rádio — Classe 1" (que o arquivo da ANAC não traz) não pode perder a
// marcação porque o CNPJ foi rebuscado.
const LISTAS_ANAC = ["categorias_classes", "capacidades", "convalidacoes"];
const juntarAnac = (perfil, patch) => {
  const out = { ...perfil, ...patch };
  for (const k of LISTAS_ANAC) {
    if (patch[k]) out[k] = Array.from(new Set([...(Array.isArray(perfil[k]) ? perfil[k] : []), ...patch[k]]));
  }
  return out;
};

// Rascunho do fornecedor: vale 3 dias neste aparelho, um por link.
const RASCUNHO_VALIDADE = 3 * 24 * 60 * 60 * 1000;
const NAVY = "#251C59";
const ROXO = "#7c3aed";

const empEmpty = {
  razaoSocial: "", nomeFantasia: "", cnpj: "", inscricaoEstadual: "", simplesNacional: "", tipoEstabelecimento: "oficina",
  atividade: "", rua: "", numero: "", bairro: "", cidade: "", estado: "", cep: "",
  telefone: "", email: "",
};
const respEmpty = { nome: "", doc: "", rg: "", nascimento: "", cargo: "", celular: "", email: "" };
// Representante legal da EMPRESA — quem assina pode ser outra pessoa (gerente,
// preposto). Vem na 2ª etapa, junto do documento dele.
const repEmpty = { nome: "", cargo: "" };
const bancoEmpty = { banco: "", agencia: "", contaCorrente: "", tipoConta: "", titularConta: "", chavePix: "" };
const socEmpty = {
  fazSocorro: null, atende24h: false, assistenciaRodovias: false,
  tiposAssistencia: [], diasAtendimento: [], horarioInicio: "", horarioFim: "",
  telefoneSocorro: "", rodoviasAtendidas: "",
};

export default function PublicFicha({ token }) {
  const info = decodeFichaToken(token) || {};
  const header = info.h || null;
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";
  const isMobile = typeof navigator !== "undefined" && /android|iphone|ipad|ipod|mobile|blackberry|iemobile|opera mini/i.test(navigator.userAgent || "");

  // Modelo do formulário (Manutenção / Combustível / Aeronáutico). Vem do link
  // (`t`); sem ele, sai dos sistemas — link só de combustível já nasce como
  // posto, como antes, e a busca por CNPJ ainda pode corrigir o ramo. Na
  // correção, a ficha salva decide (ver o carregamento abaixo).
  const [modelo, setModelo] = useState(() => modeloDaFicha({ t: info.t, sistemas: info.s }));
  const [sisFicha, setSisFicha] = useState(Array.isArray(info.s) ? info.s : []);
  const [emp, setEmp] = useState(() => ({ ...empEmpty, tipoEstabelecimento: modelo.tipoPadrao }));
  const [resp, setResp] = useState({ ...respEmpty });
  const [rep, setRep] = useState({ ...repEmpty });
  const [banco, setBanco] = useState({ ...bancoEmpty });
  const [servicos, setServicos] = useState([]);
  const [tiposVeiculo, setTiposVeiculo] = useState([]);
  const [perfil, setPerfil] = useState({});
  // Link com convite (tabela comercial escolhida pelo comercial): mostra o
  // quadro dessa tabela. O que vale no banco é decidido pelo gatilho, que lê o
  // MESMO convite — editar o link ou o navegador não muda a tabela gravada.
  useEffect(() => {
    if (!info.tc || (info.c && info.k)) return;
    let vivo = true;
    tabelaDoConvite(info.tc).then((tab) => {
      if (vivo && tab) setPerfil((p) => ({ ...p, tabelaComercial: tab, convite: info.tc }));
    });
    return () => { vivo = false; };
  }, []); // eslint-disable-line
  // Estados com taxa definida no cadastro: sem condição (sem convite), o posto
  // vê "condições a definir" e envia a ficha sem escolher taxa. O banco
  // confere de novo (gatilho) — esta marca é só para a tela.
  const [ufsExclusivas, setUfsExclusivas] = useState([]);
  useEffect(() => { ufsCondicaoExclusiva().then(setUfsExclusivas); }, []);
  const aDefinir = emp.tipoEstabelecimento === "posto" && condicoesADefinir(emp.estado, perfil, ufsExclusivas);
  useEffect(() => {
    if (!!perfil.condicoesADefinir === aDefinir) return;
    setPerfil((p) => {
      const n = { ...p };
      if (aDefinir) n.condicoesADefinir = true; else delete n.condicoesADefinir;
      return n;
    });
  }, [aDefinir]); // eslint-disable-line
  const [anpInfo, setAnpInfo] = useState(null);
  // Ramo definido pela atividade do CNPJ fica TRAVADO: o seletor fica logo
  // acima das perguntas que ele controla, e um toque errado no celular trocava
  // o ramo — as respostas do ramo anterior são descartadas de propósito, então
  // o engano apagava o que a pessoa já tinha preenchido. Destravar é um ato
  // explícito, e fica registrado na trilha da ficha.
  const [tipoTravado, setTipoTravado] = useState(false);
  const [tipoDestravado, setTipoDestravado] = useState(null);
  const [anacInfo, setAnacInfo] = useState(null);
  const [verTodosServicos, setVerTodosServicos] = useState(false);
  const [soc, setSoc] = useState({ ...socEmpty });

  const [open, setOpen] = useState({ empresa: true, responsavel: true, banco: false, servicos: true, socorro: true });
  const [cpfMsg, setCpfMsg] = useState("");
  const [buscandoCpf, setBuscandoCpf] = useState(false);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [aceite, setAceite] = useState({ pronto: false, aceites: [] });
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [corr, setCorr] = useState(null); // { id, token } quando é correção
  const [corrErr, setCorrErr] = useState("");
  const corrRowRef = useRef(null);
  const idRef = useRef(generateId());
  const tipoTocado = useRef(false); // escolha manual do ramo vence a detecção pelo CNPJ
  // O token nasce aqui, junto do id, porque logo após o INSERT o navegador
  // precisa dele para provar à serverless que é o dono desta ficha — e o
  // default do banco (gen_random_uuid) o navegador não tem como ler.
  const tokenRef = useRef(generateId().replace(/-/g, ""));

  // O link diz para quais sistemas a empresa entra; daí sai o formulário.
  // Posto que entra pelo combustível não responde catálogo de oficina nem
  // "tipos de veículo atendidos" — responde bandeira, combustíveis e ANP.
  const secoes = secoesDaFicha(sisFicha, emp.tipoEstabelecimento);
  // Link de combustível em empresa que o CNPJ não classificou como posto: as
  // perguntas do posto ainda são as certas — é para o combustível que ela vai.
  const tipoPerfil = perfilDe(emp.tipoEstabelecimento) ? emp.tipoEstabelecimento : (secoes.posto ? "posto" : emp.tipoEstabelecimento);

  // Ramo escolhido → quais perguntas extras a ficha faz (null = ficha padrão)
  const perfilAtual = perfilDe(tipoPerfil);
  // Socorro e catálogo seguem o `secoesDaFicha` — inclusive o "não" da oficina
  // aeronáutica, que responde atendimento fora de sede no bloco ANAC.
  const temSocorro = secoes.socorro;
  const tituloServicos = tituloSecaoServicos(secoes, tipoPerfil);
  // O link Aeronáutico só oferece a oficina aeronáutica; os outros, os ramos
  // terrestres. O ramo já salvo aparece sempre, para não sumir da tela.
  const tiposDoModelo = TIPOS_ESTABELECIMENTO.filter((t) => modelo.tipos.includes(t.id) || t.id === emp.tipoEstabelecimento);

  // 2ª etapa: a ficha JÁ existe (link de correção/documentos). É a hora de pedir
  // os documentos da empresa — na 1ª etapa eles só atrasariam o cadastro.
  const etapa2 = !!corr;
  // Link mandado só para completar documento (flag `d` no token): muda o texto,
  // não o formulário — quem quiser corrigir um dado no meio do caminho, corrige.
  const soDocs = !!info.d;
  // Já aceitou antes e voltou só para mandar documento: o aceite continua
  // valendo (ele não reassina, e não se grava um segundo aceite).
  const aceiteJaFeito = soDocs && !!corrRowRef.current?.aceite_termos;

  // Quem assinou é o próprio representante legal? Vem marcado sozinho quando o
  // nome bate com o administrador que veio do CNPJ — o caso comum.
  // Ramo e perfil entram porque a oficina aeronáutica deve COM/EO na 2ª etapa.
  const fichaAtual = { responsavelNome: resp.nome, representanteNome: rep.nome, tipoEstabelecimento: emp.tipoEstabelecimento, perfil };
  const souEuMesmo = assinanteEhRepresentante(fichaAtual);
  // Marcar copia os dados de quem assinou para o representante (é a mesma
  // pessoa); desmarcar limpa, para ele dizer quem é o outro.
  const marcarMesmaPessoa = (marcado) =>
    setRep(marcado ? { nome: resp.nome.trim(), cargo: resp.cargo.trim() } : { ...repEmpty });

  const setE = (patch) => setEmp((p) => ({ ...p, ...patch }));
  const setR = (patch) => setResp((p) => ({ ...p, ...patch }));
  const setRp = (patch) => setRep((p) => ({ ...p, ...patch }));
  const setB = (patch) => setBanco((p) => ({ ...p, ...patch }));
  const setS = (patch) => setSoc((p) => ({ ...p, ...patch }));
  const toggleIn = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const toggleSec = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  // Limpar o perfil não pode levar junto o convite: é ele que diz ao banco
  // qual tabela comercial o comercial escolheu para este link.
  const soOConvite = (p) => (p?.convite ? { convite: p.convite, tabelaComercial: p.tabelaComercial } : {});
  // Trocar o ramo troca as perguntas. O que foi respondido para o ramo anterior
  // (bandeira de posto, capacidade de guincho) não vale para o novo — guardar
  // isso viraria dado errado dentro da ficha.
  const trocarTipo = (tipo) => {
    const def = perfilDe(tipo);
    tipoTocado.current = true;
    setE({ tipoEstabelecimento: tipo });
    setPerfil(soOConvite);
    setVerTodosServicos(false);
    if (def && def.veiculos === false) setTiposVeiculo([]);
    setOpen((p) => ({ ...p, servicos: true }));
  };

  const handlePickDoc = async (tipo, files) => {
    if (!files.length || preview) return;
    setErr(""); setProcessing(true);
    for (const original of files) {
      // Foto do celular reduzida (sobe rápido no 4G, HEIC vira JPEG) e
      // extensão limpa — ver prepararUpload.
      const { file: f, ext, contentType } = await prepararUpload(original);
      const path = `documentos/${idRef.current}-${generateId().slice(0, 8)}.${ext}`;
      try {
        await uploadToStorage(path, f, contentType, { upsert: false });
        setPendingDocs((prev) => [...prev, { tipo, path, nome: f.name || `documento.${ext}` }]);
      } catch { setErr("Falha ao enviar o arquivo. Verifique a conexão e tente novamente."); }
    }
    setProcessing(false);
  };
  const removeDoc = (path) => setPendingDocs((prev) => prev.filter((d) => d.path !== path));

  const handleCNPJ = async (val) => {
    const m = maskCNPJ(val); setE({ cnpj: m });
    if (m.replace(/\D/g, "").length === 14) {
      const r = await fetchCNPJ(m);
      if (r) {
        // O ramo vem da atividade do CNPJ — mas só enquanto ninguém tiver
        // escolhido à mão, senão a busca desfaria a escolha da pessoa.
        // E só para um ramo que o formulário oferece: o CNAE de uma oficina não
        // transforma o link de posto em ficha de oficina.
        const achado = tipoTocado.current ? null : tipoPorAtividade(r.atividadePrincipal);
        // A exceção é a aeronáutica: CNAE de manutenção de aeronave liga o bloco
        // ANAC mesmo num link de manutenção — perguntar catálogo de oficina de
        // carro a um hangar é o erro que este formulário existe para evitar.
        const detectado = achado && (modelo.tipos.includes(achado) || achado === "oficina_aeronautica") ? achado : null;
        if (detectado && detectado !== emp.tipoEstabelecimento) {
          setPerfil(soOConvite);
          if (perfilDe(detectado)?.veiculos === false) setTiposVeiculo([]);
        }
        if (detectado) setTipoTravado(true);
        setEmp((p) => ({
          ...p,
          ...(detectado ? { tipoEstabelecimento: detectado } : {}),
          razaoSocial: r.razaoSocial || p.razaoSocial, nomeFantasia: r.nomeFantasia || p.nomeFantasia,
          rua: r.rua || p.rua, numero: r.numero || p.numero, bairro: r.bairro || p.bairro,
          cidade: r.cidade || p.cidade, estado: r.uf || p.estado, cep: r.cep || p.cep,
          atividade: r.atividadePrincipal || p.atividade, simplesNacional: r.simplesNacional || p.simplesNacional,
          inscricaoEstadual: p.inscricaoEstadual || r.inscricaoEstadual,
          telefone: p.telefone || r.telefone || "", email: p.email || r.email || "",
        }));
        if (r.celular) setResp((p) => ({ ...p, celular: p.celular || r.celular }));
        // O administrador do quadro societário é o representante legal. Vem
        // sozinho do CNPJ — e é o que permite saber, depois, se quem assinou é
        // ele mesmo (aí não se pede documento nenhum a mais).
        if (r.representanteNome) setRep((p) => ({ nome: p.nome || r.representanteNome, cargo: p.cargo || r.representanteCargo || "" }));
        setOpen((p) => ({ ...p, empresa: true }));
        // Posto: busca na ANP (via proxy) e preenche autorização, bandeira e combustíveis
        const tipoFinal = detectado || emp.tipoEstabelecimento;
        setAnpInfo(null);
        setAnacInfo(null);
        if (tipoFinal === "posto") {
          const anp = await fetchANP(m);
          if (anp) { setPerfil((p) => ({ ...p, ...anpParaPerfil(anp) })); setAnpInfo(anp); }
        }
        // Oficina de aeronave: busca o COM nos dados abertos da ANAC.
        if (tipoFinal === "oficina_aeronautica") {
          const anac = await fetchANAC(m);
          if (anac) { setPerfil((p) => juntarAnac(p, anacParaPerfil(anac))); setAnacInfo(anac); }
        }
      }
    }
  };

  const buscarCPF = async () => {
    setCpfMsg("");
    setBuscandoCpf(true);
    const r = await fetchCPF(resp.doc);
    setBuscandoCpf(false);
    if (!r) { setCpfMsg("Informe um CPF válido (11 dígitos)."); return; }
    if (r.error === "not_configured") { setCpfMsg("Busca automática de CPF indisponível — preencha nome e nascimento manualmente."); return; }
    if (r.error) { setCpfMsg("Não foi possível consultar o CPF agora."); return; }
    if (r.nome && !resp.nome) setR({ nome: r.nome });
    if (r.nascimento) setR({ nascimento: r.nascimento });
    if (!r.nome && !r.nascimento) setCpfMsg("CPF não encontrado.");
  };

  const handleCEP = async (val) => {
    const m = maskCEP(val); setE({ cep: m });
    if (m.replace(/\D/g, "").length === 8) {
      const r = await fetchCEP(m);
      if (r) setEmp((p) => ({ ...p, rua: r.rua || p.rua, bairro: r.bairro || p.bairro, cidade: r.cidade || p.cidade, estado: r.uf || p.estado }));
    }
  };

  // Envio que ficou preso numa tentativa anterior (rede caiu, servidor fora)
  // sobe agora, sem o fornecedor precisar refazer nada. Silencioso de
  // propósito: quem abriu o link veio preencher, não resolver problema nosso.
  useEffect(() => { reenviarPendentes().catch(() => null); }, []);

  // ── Rascunho no aparelho ─────────────────────────────────────────
  // No celular o Android fecha o navegador quando falta memória (ao abrir a
  // câmera, ao trocar de app para achar um documento) e a aba volta
  // recarregada: o fornecedor perdia a ficha inteira e, muitas vezes,
  // desistia. Tudo o que ele preenche fica guardado aqui até enviar. O aceite
  // dos termos NÃO volta: é evidência do momento em que foi dado.
  const chaveRascunho = `ficha:${token}`;
  const rascunhoPronto = useRef(false);
  const aplicarRascunho = () => {
    const r = lerRascunho(chaveRascunho, RASCUNHO_VALIDADE);
    if (!r) return false;
    if (r.id) idRef.current = r.id;
    if (r.tk) tokenRef.current = r.tk;
    if (r.tipoTocado) tipoTocado.current = true;
    if (Array.isArray(r.sisFicha)) setSisFicha(r.sisFicha);
    if (r.emp) setEmp((p) => ({ ...p, ...r.emp }));
    if (r.resp) setResp((p) => ({ ...p, ...r.resp }));
    if (r.rep) setRep((p) => ({ ...p, ...r.rep }));
    if (r.banco) setBanco((p) => ({ ...p, ...r.banco }));
    if (Array.isArray(r.servicos)) setServicos(r.servicos);
    if (Array.isArray(r.tiposVeiculo)) setTiposVeiculo(r.tiposVeiculo);
    if (r.perfil && typeof r.perfil === "object") setPerfil(r.perfil);
    if (r.soc) setSoc((p) => ({ ...p, ...r.soc }));
    if (Array.isArray(r.pendingDocs)) setPendingDocs(r.pendingDocs);
    return true;
  };
  // Link novo: recupera já. Link de correção/documentos: espera a ficha do
  // banco carregar e o rascunho vai por cima (é o que ele digitou depois).
  useEffect(() => {
    if (preview) return;
    if (!(info.c && info.k)) { aplicarRascunho(); rascunhoPronto.current = true; }
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!rascunhoPronto.current || done || preview) return;
    const preencheu = resp.nome || emp.cnpj || emp.razaoSocial || emp.nomeFantasia || pendingDocs.length;
    if (!preencheu) return;
    gravarRascunho(chaveRascunho, {
      id: idRef.current, tk: tokenRef.current, tipoTocado: tipoTocado.current, sisFicha,
      emp, resp, rep, banco, servicos, tiposVeiculo, perfil, soc, pendingDocs,
    });
  }, [emp, resp, rep, banco, servicos, tiposVeiculo, perfil, soc, pendingDocs, sisFicha, done]); // eslint-disable-line
  useEffect(() => { if (done) apagarRascunho(chaveRascunho); }, [done]); // eslint-disable-line

  // Modo correção: carrega a ficha existente (via link com código) e pré-preenche
  useEffect(() => {
    const fid = info.c, tk = info.k;
    if (!fid || !tk) return;
    (async () => {
      try {
        // Ficha CONCLUÍDA recusa a RPC de correção — e é justamente depois de
        // concluir que se pedem os documentos da empresa. O link de documentos
        // usa a RPC própria, que lê sempre e grava só anexo/representante.
        const rpc = info.d ? "ficha_para_documentos" : "ficha_para_correcao";
        const { data, error } = await supabase.rpc(rpc, { p_id: fid, p_token: tk });
        if (error || !data) { setCorrErr(info.d ? "Link de documentos inválido ou expirado." : "Link de correção inválido ou cadastro já concluído."); return; }
        corrRowRef.current = data;
        // Ficha já existente: o ramo salvo manda, mesmo se o CNPJ for rebuscado.
        tipoTocado.current = true;
        // E o modelo do formulário é o da ficha — o link de correção antigo não
        // traz `t`, e um posto não pode reabrir com cara de oficina.
        const sisSalvos = Array.isArray(data.sistemas) ? data.sistemas : [];
        setSisFicha(sisSalvos);
        setModelo(modeloDaFicha({ t: info.t, tipo: data.tipo_estabelecimento, sistemas: sisSalvos }));
        setEmp({
          razaoSocial: data.razao_social || "", nomeFantasia: data.nome_fantasia || "", cnpj: data.cnpj || "",
          inscricaoEstadual: data.inscricao_estadual || "", simplesNacional: data.simples_nacional || "",
          tipoEstabelecimento: data.tipo_estabelecimento || "oficina", atividade: data.atividade || "",
          rua: data.rua || "", numero: data.numero || "", bairro: data.bairro || "",
          cidade: data.cidade || "", estado: data.estado || "", cep: data.cep || "",
          telefone: data.telefone || "", email: data.email || "",
        });
        setResp({
          nome: data.responsavel_nome || "", doc: data.responsavel_doc || "", rg: data.responsavel_rg || "",
          nascimento: data.responsavel_nascimento || "", cargo: data.responsavel_cargo || "",
          celular: data.celular || data.responsavel_telefone || "", email: data.responsavel_email || "",
        });
        setRep({ nome: data.representante_nome || "", cargo: data.representante_cargo || "" });
        setBanco({
          banco: data.banco || "", agencia: data.agencia || "", contaCorrente: data.conta_corrente || "",
          tipoConta: data.tipo_conta || "", titularConta: data.titular_conta || "", chavePix: data.chave_pix || "",
        });
        setServicos(Array.isArray(data.servicos) ? data.servicos : []);
        setTiposVeiculo(Array.isArray(data.tipos_veiculo) ? data.tipos_veiculo : []);
        setPerfil(data.perfil && typeof data.perfil === "object" ? data.perfil : {});
        setSoc({
          fazSocorro: typeof data.faz_socorro === "boolean" ? data.faz_socorro : null,
          atende24h: !!data.atende_24h, assistenciaRodovias: !!data.assistencia_rodovias,
          tiposAssistencia: Array.isArray(data.tipos_assistencia) ? data.tipos_assistencia : [],
          diasAtendimento: Array.isArray(data.dias_atendimento) ? data.dias_atendimento : [],
          horarioInicio: data.horario_inicio || "", horarioFim: data.horario_fim || "",
          telefoneSocorro: data.telefone_socorro || "", rodoviasAtendidas: data.rodovias_atendidas || "",
        });
        setPendingDocs(Array.isArray(data.documentos) ? data.documentos : []);
        // Link só de documentos: abre na seção dos documentos e deixa o resto
        // fechado — quem veio mandar uma foto não precisa rolar a ficha inteira.
        const soDoc = !!info.d;
        setOpen({ empresa: !soDoc, responsavel: true, banco: !soDoc, servicos: !soDoc, socorro: !soDoc });
        setCorr({ id: fid, token: tk });
        aplicarRascunho();
        rascunhoPronto.current = true;
      } catch { setCorrErr("Não foi possível carregar o cadastro para correção."); }
    })();
  }, []); // eslint-disable-line

  // O fornecedor NÃO é criado nos sistemas por preencher o link — isso é ato do
  // comercial, no botão "Cadastrar aqui e gerar link" do detalhe da ficha. Aqui
  // só salvamos a ficha e registramos o aceite (que grava apenas no CRM). Criar
  // fornecedor em produção a partir de um envio anônimo seria abrir a porta para
  // qualquer um escrever nos sistemas.

  const submit = async () => {
    if (preview) return;
    setErr("");
    if (!resp.nome.trim()) { setOpen((p) => ({ ...p, responsavel: true })); return setErr("Informe o nome completo do responsável."); }
    // O ÚNICO documento que trava é o de quem assina — é a prova de quem
    // aceitou os termos. Os da empresa vêm depois, pelo link de documentos.
    if (isMobile && !pendingDocs.some((d) => d.tipo === DOC_ASSINANTE.tipo)) { setOpen((p) => ({ ...p, responsavel: true })); return setErr("Envie a foto do documento de quem está assinando."); }
    // Link só de documentos não cobra o resto da ficha: quem abriu para mandar
    // uma foto não pode esbarrar numa pergunta de socorro 24h.
    // Os obrigatórios do ramo (na aeronáutica, os da ANAC: situação, COM,
    // categorias, RT, aeronaves e capacidades). Mesma lista que a equipe vê.
    const faltaRamo = soDocs ? [] : faltandoNoPerfil(tipoPerfil, perfil);
    if (faltaRamo.length) { setOpen((p) => ({ ...p, servicos: true })); return setErr(`Preencha: ${faltaRamo.join(", ")}.`); }
    if (!soDocs && temSocorro && soc.fazSocorro === null) { setOpen((p) => ({ ...p, socorro: true })); return setErr("Responda se você presta socorro 24h ou assistência em rodovias."); }
    if (temSocorro && soc.fazSocorro) {
      if (!soc.tiposAssistencia.length) { setOpen((p) => ({ ...p, socorro: true })); return setErr("Marque pelo menos um tipo de assistência prestada."); }
      if (!soc.atende24h && (!soc.diasAtendimento.length || !soc.horarioInicio || !soc.horarioFim)) {
        setOpen((p) => ({ ...p, socorro: true }));
        return setErr("Informe os dias e o horário em que você presta o socorro.");
      }
    }
    if (!aceite.pronto && !aceiteJaFeito) return setErr("É preciso ler e aceitar os termos de credenciamento para enviar.");

    setSaving(true);
    try {
      const id = corr ? corr.id : idRef.current;
      const today = getToday();
      const assinaturaPath = null; // assinatura desenhada foi substituída pelo aceite dos termos

      let ip = null;
      try { const r = await fetch("https://api.ipify.org?format=json"); ip = (await r.json())?.ip || null; } catch { /* ignora */ }
      const ts = new Date().toISOString();

      const enderecoCompleto = [[emp.rua, emp.numero].filter(Boolean).join(", "), emp.bairro, emp.cep].filter(Boolean).join(" - ");
      // Seção que o formulário não mostrou não manda resposta: socorro "não"
      // por omissão é o que já era gravado para quem respondia "não".
      // Na correção, o que já estava gravado fica: esconder a pergunta não é
      // motivo para apagar a resposta antiga.
      const socorro = (temSocorro || corr) && soc.fazSocorro ? soc : { ...socEmpty, fazSocorro: false };
      const campos = {
        // Empresa
        razao_social: emp.razaoSocial.trim() || null,
        nome_fantasia: emp.nomeFantasia.trim() || info.n || null,
        cnpj: emp.cnpj.trim() || null,
        tipo_estabelecimento: emp.tipoEstabelecimento || null,
        inscricao_estadual: emp.inscricaoEstadual || null, simples_nacional: emp.simplesNacional || null,
        atividade: emp.atividade || null,
        rua: emp.rua || null, numero: emp.numero || null, bairro: emp.bairro || null,
        cidade: emp.cidade || null, estado: emp.estado || null, cep: emp.cep || null,
        endereco_completo: enderecoCompleto || null,
        telefone: emp.telefone || null,
        // `email` é o e-mail de contato da ficha (vira o acesso ao sistema):
        // usa o da empresa e, se não houver, o do responsável.
        email: emp.email.trim() || resp.email.trim() || null,
        // Responsável
        responsavel_nome: resp.nome.trim(),
        responsavel_doc: resp.doc.trim() || null, responsavel_rg: resp.rg.trim() || null,
        responsavel_nascimento: resp.nascimento || null, responsavel_cargo: resp.cargo.trim() || null,
        responsavel_telefone: resp.celular || null, responsavel_email: resp.email.trim() || null,
        celular: resp.celular || null,
        // Representante legal da empresa (2ª etapa) — quem assinou pode ser outro
        representante_nome: rep.nome.trim() || null, representante_cargo: rep.cargo.trim() || null,
        // Bancários
        banco: banco.banco || null, agencia: banco.agencia || null, conta_corrente: banco.contaCorrente || null,
        tipo_conta: banco.tipoConta || null, titular_conta: banco.titularConta || null, chave_pix: banco.chavePix || null,
        // Serviços
        // Só o ramo sem catálogo (aeronáutica) zera; um posto antigo que tinha
        // serviços marcados não os perde por o formulário novo escondê-los.
        servicos: perfilAtual?.catalogo === false ? [] : servicos,
        tipos_veiculo: perfilAtual?.veiculos === false ? [] : tiposVeiculo,
        // Perguntas do ramo (bandeira, combustíveis, COM/ANAC...) — ver
        // PERFIS_ESTABELECIMENTO. Só o que estava à vista vai para o banco.
        perfil: perfilAtual ? limparPerfil(tipoPerfil, perfil) : {},
        // Socorro / assistência
        faz_socorro: !!socorro.fazSocorro,
        atende_24h: !!socorro.fazSocorro && !!socorro.atende24h,
        assistencia_rodovias: !!socorro.fazSocorro && !!socorro.assistenciaRodovias,
        tipos_assistencia: socorro.fazSocorro ? socorro.tiposAssistencia : [],
        dias_atendimento: socorro.fazSocorro && !socorro.atende24h ? socorro.diasAtendimento : [],
        horario_inicio: socorro.fazSocorro && !socorro.atende24h ? socorro.horarioInicio || null : null,
        horario_fim: socorro.fazSocorro && !socorro.atende24h ? socorro.horarioFim || null : null,
        telefone_socorro: socorro.fazSocorro ? socorro.telefoneSocorro || null : null,
        rodovias_atendidas: socorro.fazSocorro && socorro.assistenciaRodovias ? socorro.rodoviasAtendidas || null : null,
        // Anexos
        documentos: pendingDocs, documento_path: pendingDocs[0]?.path || null,
        dispositivo: isMobile ? "mobile" : "desktop",
        // Aceite eletrônico dos termos (substitui a assinatura desenhada). Quem
        // já tinha aceitado e voltou só para mandar documento mantém o aceite.
        aceite_termos: aceite.pronto || aceiteJaFeito,
      };

      if (corr && soDocs) {
        // 2ª ETAPA — documentos depois do cadastro.
        //
        // Grava só o que esta tela coleta (anexos + representante legal), por
        // uma RPC que aceita ficha já concluída. E, gravado, avisa os sistemas
        // na hora: o documento tem de chegar onde a pendência aparece, senão a
        // equipe teria de abrir a ficha e clicar em "tentar de novo" para cada
        // fornecedor que mandou uma foto.
        const histAnt = Array.isArray(corrRowRef.current?.historico) ? corrRowRef.current.historico : [];
        const hist = [...histAnt, {
          ts, acao: "anexo",
          detalhe: `${pendingDocs.length} documento(s) enviado(s) pelo cliente (${isMobile ? "celular" : "computador"}). IP: ${ip || "n/d"}`,
          autor: resp.nome.trim() || corrRowRef.current?.responsavel_nome || "",
        }];
        const { data, error } = await supabase.rpc("salvar_documentos", {
          p_id: corr.id, p_token: corr.token,
          p_documentos: pendingDocs,
          p_rep_nome: rep.nome.trim(), p_rep_cargo: rep.cargo.trim(),
          p_historico: hist,
        });
        if (error) throw new Error(error.message);
        if (!data || data < 1) throw new Error("link inválido ou expirado");

        // Ficha antiga (de antes da emissão automática) ganha o Cartão CNPJ
        // agora, antes do reenvio — assim ele viaja junto. Quem já tem, não
        // gasta consulta: a serverless devolve na hora.
        await emitirCartaoCNPJ(corr.id, corr.token).catch(() => null);

        // Reenvia o cadastro (que leva os documentos) a cada sistema da ficha.
        // Falha aqui não é do fornecedor: a ficha está salva e a equipe reenvia
        // pelo botão do detalhe.
        const alvos = Array.isArray(corrRowRef.current?.sistemas) && corrRowRef.current.sistemas.length
          ? corrRowRef.current.sistemas : ["manutencao"];
        await Promise.all(alvos.map((sis) => sincronizarCadastro(corr.id, corr.token, sis).catch(() => null)));
        setDone(true);
      } else if (corr) {
        // Correção: atualiza a mesma ficha via RPC (não perde os dados)
        const histAnt = Array.isArray(corrRowRef.current?.historico) ? corrRowRef.current.historico : [];
        const p_dados = {
          ...campos,
          assinatura_path: assinaturaPath || corrRowRef.current?.assinatura_path || null,
          historico: [...histAnt, { ts, acao: soDocs ? "anexo" : "edicao", detalhe: `${soDocs ? "Documentos enviados" : "Correção enviada"} pelo cliente (${isMobile ? "celular" : "computador"}). ${aceite.pronto ? "Termos aceitos. " : ""}IP: ${ip || "n/d"}`, autor: resp.nome.trim() }],
        };
        const { data, error } = await supabase.rpc("salvar_correcao", { p_id: corr.id, p_token: corr.token, p_dados });
        if (error) throw new Error(error.message);
        if (!data || data < 1) throw new Error("cadastro não encontrado ou já concluído");
        // Sem novo aceite quando ele só voltou para mandar documento: gravar um
        // segundo aceite inventaria uma assinatura que ninguém deu hoje.
        if (aceite.pronto) {
          await registrarAceite(corr.id, { editToken: corr.token, aceitante: { nome: resp.nome.trim(), cpf: resp.doc, email: resp.email, cargo: resp.cargo }, origem: "link" }).catch(() => null);
        }
        setDone(true);
      } else {
        const row = {
          id, edit_token: tokenRef.current,
          user_id: info.u || null, partner_id: info.p || null,
          sistemas: Array.isArray(info.s) ? info.s : [], origem: "link", status: "pendente", situacao: "ATIVO", pais: "Brasil",
          ...campos,
          assinatura_path: assinaturaPath,
          notes: header ? [header.l1, header.l2].filter(Boolean).join(" | ") : null,
          historico: [
            { ts, acao: "criacao", detalhe: `Enviado via link por ${isMobile ? "celular" : "computador"}. Termos aceitos. IP: ${ip || "n/d"}`, autor: resp.nome.trim() },
            // O ramo define as perguntas e o sistema de destino. Quando o
            // fornecedor troca o que veio do CNPJ, a equipe precisa saber —
            // senão a ficha chega com o bloco "errado" e ninguém sabe por quê.
            ...(tipoDestravado && tipoDestravado !== emp.tipoEstabelecimento
              ? [{ ts, acao: "edicao", tipo: "ramo_trocado", de: tipoDestravado, para: emp.tipoEstabelecimento,
                   detalhe: `Ramo trocado à mão: o CNPJ indicava "${tipoDestravado}" e o fornecedor escolheu "${emp.tipoEstabelecimento}".`, autor: resp.nome.trim() }]
              : []),
            ...(pendingDocs.length ? [{ ts, acao: "anexo", detalhe: `${pendingDocs.length} documento(s). UA: ${navigator.userAgent}`, autor: resp.nome.trim() }] : []),
          ],
          created_at: today,
        };
        const { error } = await supabase.from("credenciamentos").insert(row);
        if (error) {
          // Guarda no navegador antes de avisar o erro: se ele fechar a página
          // agora, o cadastro sobe sozinho quando abrir o link de novo. Os
          // campos aqui já estão no formato do banco.
          enfileirar("credenciamentos", row, { emColunas: true });
          throw new Error(error.message);
        }
        await registrarAceite(id, { editToken: tokenRef.current, aceitante: { nome: resp.nome.trim(), cpf: resp.doc, email: resp.email, cargo: resp.cargo }, origem: "link" }).catch(() => null);
        // Cartão CNPJ: emitido na Receita e anexado aqui mesmo — um documento a
        // menos para pedir depois. Falha não afeta o envio: a ficha está salva.
        await emitirCartaoCNPJ(id, tokenRef.current).catch(() => null);
        setDone(true);
      }
    } catch (e) { setErr("Não foi possível enviar: " + (e.message || "tente novamente")); }
    finally { setSaving(false); }
  };

  const wrap = { minHeight: "100vh", background: "#f1f5f9", display: "flex", justifyContent: "center", padding: "24px 14px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif", color: "#334155" };
  const card = { width: "100%", maxWidth: 580, background: "#fff", borderRadius: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.08)", overflow: "hidden", height: "fit-content" };
  const btnGhost = { display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" };

  const carregandoCorrecao = info.c && info.k && !corr && !corrErr;

  if (corrErr) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: 32, textAlign: "center", maxWidth: 460 }}>
          <X size={48} color="#ef4444" style={{ margin: "0 auto 14px" }} />
          <h2 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: 19 }}>Link indisponível</h2>
          <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.5 }}>{corrErr} Peça um novo link à equipe InstaSolutions.</p>
        </div>
      </div>
    );
  }

  if (carregandoCorrecao) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: 40, textAlign: "center", maxWidth: 460 }}>
          <Loader2 size={40} color={ROXO} className="spin" style={{ margin: "0 auto 14px" }} />
          <p style={{ fontSize: 14, color: "#64748b" }}>Carregando seu cadastro para correção...</p>
        </div>
        <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  if (done) {
    return (
      <div style={wrap}>
        <div style={{ ...card, padding: 32, textAlign: "center", maxWidth: 460 }}>
          <CheckCircle2 size={56} color="#059669" style={{ margin: "0 auto 14px" }} />
          <h2 style={{ margin: "0 0 8px", color: "#0f172a", fontSize: 20 }}>{soDocs ? "Documentos enviados!" : corr ? "Correção enviada!" : "Cadastro enviado!"}</h2>
          <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.5 }}>
            {soDocs
              ? "Recebemos seus documentos com segurança e já enviamos para o seu cadastro. Se ainda faltar algum, é só voltar neste mesmo link. Obrigado!"
              : "Recebemos seus dados com segurança. Nossa equipe vai revisar o seu cadastro e, se precisar de documentos, envia o link por WhatsApp. Obrigado!"}
          </p>
        </div>
        <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  // Indicadores de preenchimento por seção (só orientam, não bloqueiam)
  const okEmpresa = !!(emp.cnpj || emp.razaoSocial || emp.nomeFantasia);
  const okResp = !!resp.nome.trim() && (!isMobile || pendingDocs.some((d) => d.tipo === DOC_ASSINANTE.tipo));
  const okBanco = !!(banco.chavePix || (banco.banco && banco.contaCorrente));

  // Catálogo da manutenção filtrado pelo ramo: um posto não precisa varrer 30
  // itens para achar os 7 que lhe dizem respeito. O que já estiver marcado
  // continua visível, senão sumiria da tela ao trocar o tipo.
  const sugeridos = perfilAtual?.servicosSugeridos;
  const temFiltroServicos = !!sugeridos?.length;
  const listaServicos = !temFiltroServicos || verTodosServicos
    ? SERVICOS_PRESTADOS
    : SERVICOS_PRESTADOS.filter((s) => sugeridos.includes(s.id) || servicos.includes(s.id));

  const okServicos = ((secoes.servicos && servicos.length > 0) || resumoPerfil(tipoPerfil, perfil).length > 0) &&
    !faltandoNoPerfil(tipoPerfil, perfil).length;
  const anacImpeditiva = anacInfo && SITUACOES_IMPEDITIVAS.includes(anacInfo.situacao);
  const okSocorro = soc.fazSocorro === false || (soc.fazSocorro === true && soc.tiposAssistencia.length > 0 && (soc.atende24h || (soc.diasAtendimento.length > 0 && soc.horarioInicio && soc.horarioFim)));

  return (
    <div style={wrap}>
      <div style={card}>
        {preview && <div style={{ background: "#f59e0b", color: "#fff", textAlign: "center", fontSize: 12, fontWeight: 700, padding: "6px" }}>PRÉVIA — o envio está desabilitado</div>}

        {/* Header */}
        <div style={{ background: NAVY, padding: "22px 20px", textAlign: "center" }}>
          <img src="/logo-horizontal-branco.png" alt="InstaSolutions" style={{ height: 40, objectFit: "contain", maxWidth: "80%" }} />
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 8 }}>{soDocs ? "Envio de documentos" : corr ? "Correção de cadastro" : modelo.titulo}</div>
          {header && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
              {header.l1 && <div style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{header.l1}</div>}
              {header.l2 && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 2 }}>{header.l2}</div>}
            </div>
          )}
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: "10px 12px", marginBottom: 18 }}>
            <ShieldCheck size={18} color="#0d9488" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12, color: "#0f766e", lineHeight: 1.4 }}>
              {soDocs
                ? <>Seu cadastro já está feito — faltam só os <strong>documentos da empresa</strong>. Envie o que tiver agora; dá para voltar neste mesmo link e mandar o resto.</>
                : corr
                ? <>Revise seus dados abaixo, <strong>complete o que falta</strong> e reenvie. Seus dados anteriores já estão preenchidos.</>
                : <>Informe o <strong>CNPJ</strong> e o cadastro se completa quase todo sozinho. {isMobile ? <>Obrigatórios: <strong>nome completo</strong> e a <strong>foto do documento de quem assina</strong>.</> : <>Obrigatório: <strong>nome completo</strong> (o documento pode ser anexado depois).</>} Os documentos da empresa a gente pede depois.</>}
            </div>
          </div>

          {/* ══ 1. DADOS DA EMPRESA ══ */}
          <Secao icon={Building2} title="Dados da empresa" done={okEmpresa} open={open.empresa} onToggle={() => toggleSec("empresa")}>
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
              <Label>Tem CNPJ? Preencha aqui e o cadastro se completa sozinho</Label>
              <input style={inp} value={emp.cnpj} onChange={(e) => handleCNPJ(e.target.value)} placeholder="00.000.000/0000-00" inputMode="numeric" />
              {emp.razaoSocial && <div style={{ fontSize: 11, color: "#0f766e", marginTop: 6 }}>✓ Encontramos: <strong>{emp.razaoSocial}</strong>{emp.atividade ? ` — ${emp.atividade}` : ""}</div>}
            </div>

            <Label>Razão social</Label>
            <input style={inp} value={emp.razaoSocial} onChange={(e) => setE({ razaoSocial: e.target.value })} />
            <Gap />
            <Label>Nome fantasia</Label>
            <input style={inp} value={emp.nomeFantasia} onChange={(e) => setE({ nomeFantasia: e.target.value })} />
            <Gap />
            <Grid cols="1fr 1fr">
              <div><Label>Inscrição Estadual</Label><input style={inp} value={emp.inscricaoEstadual} onChange={(e) => setE({ inscricaoEstadual: e.target.value })} placeholder="Isento, se não tiver" /></div>
              <div><Label>Optante Simples Nacional</Label><select style={inp} value={emp.simplesNacional} onChange={(e) => setE({ simplesNacional: e.target.value })}><option value="">—</option><option value="sim">Sim</option><option value="nao">Não</option></select></div>
            </Grid>
            <Gap />
            <Label>Tipo de estabelecimento</Label>
            {/* Link Aeronáutico: o ramo é fixo e aparece escrito — um seletor
                de uma opção só confunde. */}
            {tiposDoModelo.length > 1 ? (
            <select
              style={{ ...inp, ...(tipoTravado ? { background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" } : {}) }}
              value={emp.tipoEstabelecimento}
              disabled={tipoTravado}
              onChange={(e) => trocarTipo(e.target.value)}
            >
              {tiposDoModelo.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            ) : (
              <div style={{ ...inp, background: "#f1f5f9", color: "#475569" }}>{tiposDoModelo[0]?.label}</div>
            )}
            {tiposDoModelo.length <= 1 ? null : tipoTravado ? (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#64748b", lineHeight: 1.4, flex: 1, minWidth: 180 }}>
                  Definido pela atividade do seu CNPJ{emp.atividade ? ` (${emp.atividade})` : ""}. As perguntas do fim da ficha são as desse ramo.
                </span>
                <button
                  type="button"
                  onClick={() => { setTipoTravado(false); setTipoDestravado(emp.tipoEstabelecimento); tipoTocado.current = true; }}
                  style={{ ...miniBtn, borderColor: "#ddd6fe", color: "#6d28d9", whiteSpace: "nowrap" }}
                >
                  Não é esse? Trocar
                </button>
              </div>
            ) : perfilAtual ? (
              <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 6 }}>
                As perguntas do fim da ficha mudaram para o seu ramo.
              </div>
            ) : null}
            <Gap />
            <Grid cols="repeat(auto-fit, minmax(120px, 1fr))">
              <div><Label>CEP</Label><input style={inp} value={emp.cep} onChange={(e) => handleCEP(e.target.value)} placeholder="00000-000" inputMode="numeric" /></div>
              <div><Label>Rua</Label><input style={inp} value={emp.rua} onChange={(e) => setE({ rua: e.target.value })} /></div>
              <div><Label>Nº</Label><input style={inp} value={emp.numero} onChange={(e) => setE({ numero: e.target.value })} /></div>
              <div><Label>Bairro</Label><input style={inp} value={emp.bairro} onChange={(e) => setE({ bairro: e.target.value })} /></div>
              <div><Label>Cidade</Label><input style={inp} value={emp.cidade} onChange={(e) => setE({ cidade: e.target.value })} /></div>
              <div><Label>UF</Label><select style={inp} value={emp.estado} onChange={(e) => setE({ estado: e.target.value })}><option value="">—</option>{BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf}</option>)}</select></div>
            </Grid>
            <Gap />
            <Grid cols="1fr 1fr">
              <div><Label>Telefone fixo da empresa</Label><input style={inp} value={emp.telefone} onChange={(e) => setE({ telefone: maskPhone(e.target.value) })} placeholder="(00) 0000-0000" inputMode="numeric" /></div>
              <div><Label>E-mail da empresa</Label><input type="email" style={inp} value={emp.email} onChange={(e) => setE({ email: e.target.value })} placeholder="contato@empresa.com" inputMode="email" /></div>
            </Grid>
          </Secao>

          {/* ══ 2. DADOS DO RESPONSÁVEL ══ */}
          <Secao icon={UserRound} title="Dados do responsável" done={okResp} open={open.responsavel} onToggle={() => toggleSec("responsavel")}>
            <Label>Nome completo *</Label>
            <input style={inp} value={resp.nome} onChange={(e) => setR({ nome: e.target.value })} placeholder="Seu nome completo" />
            <Gap />
            <Label>CPF</Label>
            <div style={{ display: "flex", gap: 8 }}>
              <input style={{ ...inp, flex: 1 }} value={resp.doc} onChange={(e) => setR({ doc: e.target.value })} placeholder="000.000.000-00" inputMode="numeric" />
              <button type="button" onClick={buscarCPF} disabled={buscandoCpf || preview} style={{ ...btnGhost, flexShrink: 0 }}>{buscandoCpf ? <Loader2 size={16} className="spin" /> : "Buscar CPF"}</button>
            </div>
            {cpfMsg && <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>{cpfMsg}</div>}
            <Gap />
            <Grid cols="1fr 1fr">
              <div><Label>RG</Label><input style={inp} value={resp.rg} onChange={(e) => setR({ rg: e.target.value })} /></div>
              <div><Label>Data de nascimento</Label><input type="date" style={inp} value={resp.nascimento} onChange={(e) => setR({ nascimento: e.target.value })} /></div>
              <div><Label>Cargo</Label><input style={inp} value={resp.cargo} onChange={(e) => setR({ cargo: e.target.value })} placeholder="Ex: Sócio, Gerente" /></div>
              <div><Label>Celular / WhatsApp</Label><input style={inp} value={resp.celular} onChange={(e) => setR({ celular: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" inputMode="numeric" /></div>
            </Grid>
            <Gap />
            <Label>E-mail <span style={{ fontWeight: 400, color: "#94a3b8" }}>(usado para criar seu acesso ao sistema)</span></Label>
            <input type="email" style={inp} value={resp.email} onChange={(e) => setR({ email: e.target.value })} placeholder="email@exemplo.com" inputMode="email" />

            <Gap h={16} />
            <Label>Documento de quem assina {isMobile ? <span style={{ fontWeight: 400, color: "#94a3b8" }}>(obrigatório)</span> : <span style={{ fontWeight: 400, color: "#94a3b8" }}>(pode anexar depois)</span>}</Label>
            <div style={{ fontSize: 11.5, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8, padding: "8px 10px", marginBottom: 10, lineHeight: 1.4 }}>
              Só o documento de <strong>quem está assinando</strong> agora. Foto ou PDF serve.
              {!etapa2 && <> Cartão CNPJ, contrato social e documento do representante da empresa <strong>a gente pede depois</strong> — não seguram seu cadastro.</>}
            </div>
            <DocsCredenciamento
              tipos={docsDaFicha("assinatura", pendingDocs)}
              docs={pendingDocs}
              onPick={handlePickDoc}
              onRemove={removeDoc}
              isMobile={isMobile}
              processing={processing}
              disabled={preview}
              obrigatorios={isMobile ? [DOC_ASSINANTE.tipo] : []}
              outros={!etapa2}
            />

            {/* 2ª ETAPA — só no link de correção/documentos, depois do cadastro.
                Nada aqui bloqueia o envio: o cadastro já existe, isto completa. */}
            {etapa2 && (
              <>
                <Gap h={18} />
                <Label>Documentos da empresa <span style={{ fontWeight: 400, color: "#94a3b8" }}>(complete o que faltar)</span></Label>
                <div style={{ fontSize: 11.5, color: "#6d28d9", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "8px 10px", marginBottom: 10, lineHeight: 1.4 }}>
                  Seu cadastro <strong>já está feito</strong>. Estes documentos completam a habilitação da empresa — mande o que tiver agora e o resto depois.
                </div>
                {/* Quem assina normalmente É o sócio-administrador. Marcando
                    aqui, o documento do representante some da lista — já é o
                    mesmo que ele mandou na assinatura. */}
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", borderRadius: 10, border: `1px solid ${souEuMesmo ? "#99f6e4" : "#e2e8f0"}`, background: souEuMesmo ? "#f0fdfa" : "#fff", cursor: "pointer", marginBottom: 12 }}>
                  <input type="checkbox" checked={souEuMesmo} onChange={(e) => marcarMesmaPessoa(e.target.checked)} style={{ width: 17, height: 17, marginTop: 1, flexShrink: 0, accentColor: "#0d9488" }} />
                  <span style={{ fontSize: 12.5, color: souEuMesmo ? "#0f766e" : "#334155", lineHeight: 1.45 }}>
                    <strong>Sou eu quem responde pela empresa</strong> — assinei a ficha e sou o representante legal.
                    {souEuMesmo && <span style={{ display: "block", fontSize: 11, marginTop: 3 }}>Então não precisa mandar documento de novo: usamos o que você já enviou.</span>}
                  </span>
                </label>

                {!souEuMesmo && (
                  <>
                    <Grid cols="1fr 1fr">
                      <div><Label>Representante legal da empresa</Label><input style={inp} value={rep.nome} onChange={(e) => setRp({ nome: e.target.value })} placeholder="Nome de quem responde pela empresa" /></div>
                      <div><Label>Função dele na empresa</Label><input style={inp} value={rep.cargo} onChange={(e) => setRp({ cargo: e.target.value })} placeholder="Ex: Sócio-administrador, Diretor" /></div>
                    </Grid>
                    <Gap h={12} />
                  </>
                )}
                <DocsCredenciamento
                  tipos={docsDaFicha("complementares", pendingDocs, fichaAtual)}
                  docs={pendingDocs}
                  onPick={handlePickDoc}
                  onRemove={removeDoc}
                  isMobile={isMobile}
                  processing={processing}
                  disabled={preview}
                />

                {/* O mesmo aviso que foi no WhatsApp. Quem está prestes a
                    fotografar um documento pessoal precisa ler aqui o que já
                    leu lá — senão a promessa vale só no convite. */}
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <ShieldCheck size={15} color="#0d9488" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                    <strong style={{ color: "#334155" }}>Seus documentos estão protegidos.</strong> O envio é por conexão segura, os arquivos ficam guardados de forma criptografada em servidor privado e são usados apenas para o seu credenciamento, conforme a <strong>LGPD (Lei 13.709/2018)</strong>. O acesso é restrito à equipe responsável e você pode pedir a exclusão a qualquer momento.
                  </div>
                </div>
              </>
            )}

          </Secao>

          {/* ══ 3. DADOS BANCÁRIOS ══ */}
          <Secao icon={Landmark} title="Dados bancários" hint="Para o repasse dos serviços" done={okBanco} open={open.banco} onToggle={() => toggleSec("banco")}>
            <Grid cols="1fr 1fr">
              <div><Label>Banco</Label><input style={inp} value={banco.banco} onChange={(e) => setB({ banco: e.target.value })} placeholder="Ex: 341 - Itaú" /></div>
              <div><Label>Agência</Label><input style={inp} value={banco.agencia} onChange={(e) => setB({ agencia: e.target.value })} inputMode="numeric" /></div>
              <div><Label>Conta</Label><input style={inp} value={banco.contaCorrente} onChange={(e) => setB({ contaCorrente: e.target.value })} inputMode="numeric" /></div>
              <div>
                <Label>Tipo de conta</Label>
                <select style={inp} value={banco.tipoConta} onChange={(e) => setB({ tipoConta: e.target.value })}>
                  <option value="">—</option>
                  <option value="corrente">Corrente</option>
                  <option value="poupanca">Poupança</option>
                </select>
              </div>
            </Grid>
            <Gap />
            <Label>Titular da conta <span style={{ fontWeight: 400, color: "#94a3b8" }}>(se diferente da empresa)</span></Label>
            <input style={inp} value={banco.titularConta} onChange={(e) => setB({ titularConta: e.target.value })} />
            <Gap />
            <Label>Chave PIX</Label>
            <input style={inp} value={banco.chavePix} onChange={(e) => setB({ chavePix: e.target.value })} placeholder="CNPJ, e-mail, telefone ou chave aleatória" />
          </Secao>

          {/* ══ 4. SERVIÇOS PRESTADOS ══ */}
          <Secao
            icon={tipoPerfil === "oficina_aeronautica" ? Plane : secoes.servicos ? Wrench : Fuel}
            title={tituloServicos.titulo}
            hint={tituloServicos.hint}
            done={okServicos} open={open.servicos} onToggle={() => toggleSec("servicos")}
          >
            {emp.tipoEstabelecimento === "oficina_aeronautica" && anacInfo && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: anacImpeditiva ? "#fef2f2" : "#f0fdf4", border: `1px solid ${anacImpeditiva ? "#fecaca" : "#bbf7d0"}`, borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                {anacImpeditiva ? <X size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} /> : <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />}
                <div style={{ fontSize: 12, color: anacImpeditiva ? "#991b1b" : "#166534", lineHeight: 1.4 }}>
                  <strong>{anacImpeditiva ? `Certificado ${anacInfo.situacao.toLowerCase()} na ANAC.` : "Oficina encontrada na ANAC."}</strong>{" "}
                  COM {anacInfo.numeroCom} — {anacInfo.razaoSocial}{anacInfo.cidade ? ` (${anacInfo.cidade}/${anacInfo.uf})` : ""}.{" "}
                  {anacImpeditiva
                    ? "Com o COM nessa situação a oficina não pode liberar aeronave como organização certificada. Se já regularizou, envie a cópia atualizada na etapa de documentos."
                    : "Situação, COM e categorias foram preenchidos automaticamente — confira e complete Rádio, Instrumento e Acessório, que não constam no arquivo público da ANAC."}
                </div>
              </div>
            )}
            {emp.tipoEstabelecimento === "posto" && anpInfo && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.4 }}>
                  <strong>Posto encontrado na ANP.</strong> {anpInfo.razaoSocial}{anpInfo.municipio ? ` — ${anpInfo.municipio}/${anpInfo.uf}` : ""}{anpInfo.autorizacao ? ` · Autorização ${anpInfo.autorizacao}` : ""}. Bandeira e combustíveis foram preenchidos automaticamente.
                </div>
              </div>
            )}
            <PerfilCampos tipo={tipoPerfil} perfil={perfil} onChange={setPerfil} />

            {secoes.veiculos && perfilAtual?.veiculos !== false && (
              <>
                <Label>Tipos de veículo atendidos</Label>
                <Chips options={TIPOS_VEICULO} selected={tiposVeiculo} onToggle={(id) => toggleIn(tiposVeiculo, setTiposVeiculo, id)} />
                <Gap h={16} />
              </>
            )}

            {secoes.servicos && (
              <>
            <Label>Serviços de manutenção que a empresa presta</Label>
            <Chips options={listaServicos} selected={servicos} onToggle={(id) => toggleIn(servicos, setServicos, id)} min={190} />
            {temFiltroServicos && (
              <button type="button" onClick={() => setVerTodosServicos((v) => !v)} style={{ ...miniBtn, marginTop: 8, borderColor: "#ddd6fe", color: "#6d28d9" }}>
                {verTodosServicos ? "Mostrar só os do meu ramo" : `Ver todos os serviços (${SERVICOS_PRESTADOS.length})`}
              </button>
            )}
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
              {servicos.length ? `${servicos.length} serviço(s) selecionado(s).` : "Marque ao menos um serviço para agilizar o credenciamento."}
            </div>
              </>
            )}
          </Secao>

          {/* ══ 5. SOCORRO 24H / ASSISTÊNCIA EM RODOVIAS ══
              É a rede de assistência do rastreamento (e da manutenção). Link só
              de combustível não pergunta: posto não presta socorro de oficina.
              Oficina de aeronave responde o atendimento fora de sede (AOG) no
              próprio bloco ANAC. */}
          {secoes.socorro && (
          <Secao icon={LifeBuoy} title="Socorro 24h / Assistência em rodovias" done={okSocorro} open={open.socorro} onToggle={() => toggleSec("socorro")}>
            <Label>A empresa presta socorro 24h ou assistência em rodovias? *</Label>
            <div style={{ display: "flex", gap: 8 }}>
              <Escolha on={soc.fazSocorro === true} onClick={() => setS({ fazSocorro: true })}>Sim, prestamos</Escolha>
              <Escolha on={soc.fazSocorro === false} onClick={() => setSoc({ ...socEmpty, fazSocorro: false })}>Não prestamos</Escolha>
            </div>

            {soc.fazSocorro === true && (
              <>
                <Gap h={16} />
                <Label>Tipos de assistência prestada *</Label>
                <Chips options={ASSISTENCIA_TIPOS} selected={soc.tiposAssistencia} onToggle={(id) => setS({ tiposAssistencia: soc.tiposAssistencia.includes(id) ? soc.tiposAssistencia.filter((x) => x !== id) : [...soc.tiposAssistencia, id] })} min={170} />

                <Gap h={16} />
                <Label>Atendimento</Label>
                <div style={{ display: "flex", gap: 8 }}>
                  <Escolha on={soc.atende24h} onClick={() => setS({ atende24h: true })}>24 horas, todos os dias</Escolha>
                  <Escolha on={!soc.atende24h} onClick={() => setS({ atende24h: false })}>Em horário específico</Escolha>
                </div>

                {!soc.atende24h && (
                  <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a" }}>
                    <Label>Dias em que presta o socorro *</Label>
                    <Chips options={DIAS_SEMANA} selected={soc.diasAtendimento} onToggle={(id) => setS({ diasAtendimento: soc.diasAtendimento.includes(id) ? soc.diasAtendimento.filter((x) => x !== id) : [...soc.diasAtendimento, id] })} min={70} />
                    <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                      <button type="button" onClick={() => setS({ diasAtendimento: DIAS_UTEIS })} style={miniBtn}>Seg a Sex</button>
                      <button type="button" onClick={() => setS({ diasAtendimento: DIAS_SEMANA.map((d) => d.id) })} style={miniBtn}>Todos os dias</button>
                    </div>
                    <Gap />
                    <Grid cols="1fr 1fr">
                      <div><Label>Das *</Label><input type="time" style={inp} value={soc.horarioInicio} onChange={(e) => setS({ horarioInicio: e.target.value })} /></div>
                      <div><Label>Até *</Label><input type="time" style={inp} value={soc.horarioFim} onChange={(e) => setS({ horarioFim: e.target.value })} /></div>
                    </Grid>
                    <div style={{ fontSize: 11, color: "#92400e", marginTop: 8, lineHeight: 1.4 }}>
                      Esse horário é o que aparece para a central de rastreamento acionar você.
                    </div>
                  </div>
                )}

                <Gap h={16} />
                <Label>Telefone de plantão <span style={{ fontWeight: 400, color: "#94a3b8" }}>(se diferente do comercial)</span></Label>
                <input style={inp} value={soc.telefoneSocorro} onChange={(e) => setS({ telefoneSocorro: maskPhone(e.target.value) })} placeholder="(00) 00000-0000" inputMode="numeric" />

                <Gap h={14} />
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={soc.assistenciaRodovias} onChange={(e) => setS({ assistenciaRodovias: e.target.checked })} style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: "#334155", lineHeight: 1.4 }}>Atendemos ocorrências <strong>em rodovia</strong> (fora da oficina)</span>
                </label>
                {soc.assistenciaRodovias && (
                  <div style={{ marginTop: 10 }}>
                    <Label>Rodovias / trechos atendidos</Label>
                    <textarea style={{ ...inp, minHeight: 64, resize: "vertical" }} value={soc.rodoviasAtendidas} onChange={(e) => setS({ rodoviasAtendidas: e.target.value })} placeholder="Ex: BR-116 km 200 ao 280, SP-070, anel viário" />
                  </div>
                )}
              </>
            )}

            {soc.fazSocorro === false && (
              <div style={{ marginTop: 10, fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
                Sem problema — você segue credenciado para os serviços marcados acima.
              </div>
            )}
          </Secao>
          )}

          {/* ══ ACEITE DOS TERMOS E ENVIO ══
              Quem já aceitou e só voltou para mandar documento não reassina: o
              aceite dele continua valendo, e pedir de novo é atrito à toa. */}
          {aceiteJaFeito ? (
            <div style={{ marginTop: 18, display: "flex", gap: 8, alignItems: "flex-start", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 12px" }}>
              <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.4 }}>
                <strong>Termos já aceitos.</strong> Não precisa aceitar de novo — é só enviar os documentos.
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 18 }}>
              <TermosAceite sistemas={info.s} onChange={setAceite} />
            </div>
          )}

          {err && <div style={{ fontSize: 13, color: "#ef4444", marginTop: 12, fontWeight: 500 }}>{err}</div>}

          <button onClick={submit} disabled={saving || processing || preview} style={{ width: "100%", marginTop: 18, padding: "13px", borderRadius: 12, border: "none", background: (saving || processing || preview) ? "#a78bfa" : NAVY, color: "#fff", fontSize: 15, fontWeight: 700, cursor: (saving || processing || preview) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {saving ? <><Loader2 size={18} className="spin" /> Enviando...</> : preview ? "Envio desabilitado (prévia)" : soDocs ? "Enviar documentos" : corr ? "Reenviar correção" : "Enviar cadastro"}
          </button>
          <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginTop: 10 }}>Envio protegido. Data e origem são registradas para sua segurança.</div>
        </div>
      </div>
      <style>{`.spin { animation: spin 1s linear infinite } @keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ── Estilos e blocos reutilizados ──
const inp = { width: "100%", padding: "11px 12px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 15, outline: "none", boxSizing: "border-box", background: "#f8fafc", fontFamily: "inherit", color: "#334155" };
const miniBtn = { padding: "5px 10px", borderRadius: 7, border: "1px solid #fcd34d", background: "#fff", color: "#92400e", fontSize: 11, fontWeight: 600, cursor: "pointer" };

function Label({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>{children}</div>;
}
function Gap({ h = 12 }) {
  return <div style={{ height: h }} />;
}
function Grid({ cols, children }) {
  return <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10 }}>{children}</div>;
}

function Secao({ icon: Icon, title, hint, done, open, onToggle, children }) {
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 12, overflow: "hidden" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 14px", border: "none", background: open ? "#faf8ff" : "#f8fafc", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: done ? "#059669" : "#ede9fe", color: done ? "#fff" : "#6d28d9" }}>
          {done ? <Check size={15} /> : <Icon size={15} />}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{title}</span>
          {hint && <span style={{ display: "block", fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{hint}</span>}
        </span>
        {open ? <ChevronUp size={17} color="#94a3b8" /> : <ChevronDown size={17} color="#94a3b8" />}
      </button>
      {open && <div style={{ padding: "14px" }}>{children}</div>}
    </div>
  );
}

function Escolha({ on, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1, padding: "12px 10px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: on ? 700 : 500,
        border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff",
        color: on ? "#6d28d9" : "#475569", fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

function Chips({ options, selected, onToggle, min = 150 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 6 }}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button key={o.id} type="button" onClick={() => onToggle(o.id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, textAlign: "left", border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569", fontWeight: on ? 600 : 400, fontFamily: "inherit" }}>
            <span style={{ width: 15, height: 15, borderRadius: 4, flexShrink: 0, border: on ? "none" : "1.5px solid #cbd5e1", background: on ? "#7c3aed" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, lineHeight: 1 }}>{on ? "✓" : ""}</span>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
